import { useEffect, useId, useRef, useState } from "react";
import { toast } from "sonner";
import { ClipboardList, ListPlus, Mail, Search, Sparkles, WandSparkles } from "lucide-react";
import { CreateListItemPayload, ItemSearchResult } from "modules/lists/types/lists";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Kbd } from "@/components/ui/kbd";
import { Button } from "@/components/ui/button";
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    useCarousel,
} from "@/components/ui/carousel";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ProductSearchPicker } from "modules/lists/components/product-search-picker";
import { Payload } from "modules/cart/types/cart";


import { searchItem } from "modules/lists/services/listService";
import { getVisitorId } from "utils/analytics";

// ─────────────────────────────────────────────────────────────────────────────
// Placeholder rotation
// ─────────────────────────────────────────────────────────────────────────────

/** Suggested search terms cycled as the idle placeholder while the input is empty and unfocused. */
export const PLACEHOLDER_SUGGESTIONS = [
    "leche sin lactosa",
    "arroz basmati 1 kg",
    "Coca-Cola Zero 1,5 L",
    "queso mantecoso",
    "huevos grandes 12 un",
    "pan de molde integral",
    "pechuga de pollo",
    "papel higiénico 40 metros",
    "detergente Omo",
    "café Nescafé",
    "tallarines Carozzi",
    "yogurt Soprole sin azúcar",
    "atún en agua",
    "aceite vegetal 1 litro",
    "jamón de pavo",
    "leche Colun o Soprole",
    "Coca-Cola Zero de 1,5 o 2 L",
    "papel higiénico Confort 40 m o más",
    "queso gauda 500 g aprox",
    "detergente Omo o Ariel de 3 L aprox",
] as const;

/** Returns a random suggestion different from the current one, when possible. */
const pickRandomPlaceholder = (current: string): string => {
    if (PLACEHOLDER_SUGGESTIONS.length <= 1) return PLACEHOLDER_SUGGESTIONS[0] ?? "";
    const candidates = PLACEHOLDER_SUGGESTIONS.filter((p) => p !== current);
    return candidates[Math.floor(Math.random() * candidates.length)];
};

/** Static instruction shown in badge mode — never rotates. */
const FIXED_PLACEHOLDER = "Escribe el producto y las características que necesitas.";

/** Number of suggestion badges rendered at once in badge mode. */
const SUGGESTION_BADGE_COUNT = 3;

/**
 * Picks `count` distinct random suggestions from `PLACEHOLDER_SUGGESTIONS`.
 * Called once when the component mounts — the returned set stays static for the
 * lifetime of the mounted component (no rotation).
 * @param count - How many distinct suggestions to pick
 * @returns A stable readonly list of distinct suggestions
 */
const pickSuggestionSet = (count: number): readonly string[] => {
    const shuffled = [...PLACEHOLDER_SUGGESTIONS];
    for (let i = shuffled.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled.slice(0, Math.min(count, shuffled.length));
};

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Stable identity for a pending save. Lets us ignore rapid duplicate submissions of
 * the SAME selection (e.g. double Enter) while still allowing a different product
 * to be registered while the previous save is still in flight.
 */
const buildPayloadKey = (item: CreateListItemPayload): string =>
    JSON.stringify([
        item.catalogProductId ?? "free",
        item.rawDescription?.trim() ?? "",
        item.quantity ?? 1,
        item.unit ?? "",
    ]);

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

interface ResultRowProps {
    product: ItemSearchResult;
    onAdd: () => void;
    isHighlighted?: boolean;
}

/** Single product result row — click anywhere to add immediately */
const ResultRow: React.FC<ResultRowProps> = ({ product, onAdd, isHighlighted }) => (
    <button
        type="button"
        onClick={onAdd}
        className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-muted active:bg-muted/70${
            isHighlighted ? " bg-orange-50" : ""
        }`}
    >
        {/* Thumbnail */}
        <div className="h-10 w-10 shrink-0 overflow-hidden rounded-md bg-muted flex items-center justify-center">
            {product.imageUrl ? (
                <img
                    src={product.imageUrl}
                    alt={product.name}
                    className="h-full w-full object-contain mix-blend-multiply"
                />
            ) : (
                <span className="text-xs text-muted-foreground">—</span>
            )}
        </div>

        {/* Info */}
        <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium leading-snug">{product.name}</p>
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                <span className="text-xs text-muted-foreground">{product.brand}</span>
                {(product.total != null || product.unit) && (
                    <span className="text-xs text-muted-foreground">
                        · {[product.total, product.unit].filter(Boolean).join(" ")}
                    </span>
                )}
            </div>
        </div>
    </button>
);

interface SinPreferenciaRowProps {
    onAdd: () => void;
    isHighlighted?: boolean;
    /** Current search query — shown in the row title so the user knows what Enter adds */
    query: string;
}

/** "Sin preferencia" row — click (or Enter) to add without linking to a specific product */
const SinPreferenciaRow: React.FC<SinPreferenciaRowProps> = ({ onAdd, isHighlighted, query }) => (
    <button
        type="button"
        onClick={onAdd}
        className={`flex w-full items-center gap-3 rounded-lg bg-primary/5 px-3 py-2.5 text-left transition-colors hover:bg-primary/10 active:bg-primary/15${
            isHighlighted ? " bg-orange-50" : ""
        }`}
    >
        <div className="h-10 w-10 shrink-0 flex items-center justify-center rounded-md bg-muted">
            <Sparkles className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">Agregar "{query}"</p>
            <p className="text-xs text-muted-foreground">Encontraremos la opción más económica</p>
        </div>
        <span className="shrink-0">
            <Kbd>Enter</Kbd>
        </span>
    </button>
);

interface BuscarMasRowProps {
    onClick: () => void;
}

/** "Buscar producto específico" row — opens the full product search picker */
const BuscarMasRow: React.FC<BuscarMasRowProps> = ({ onClick }) => (
    <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors border-t mt-1 hover:bg-muted active:bg-muted/70"
    >
        <div className="h-10 w-10 shrink-0 flex items-center justify-center rounded-md bg-muted">
            <Search className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">Buscar producto específico</p>
            <p className="text-xs text-muted-foreground">Explorar el catálogo completo</p>
        </div>
    </button>
);

interface QuickEntryMenuProps {
    /** Fires the "seed example list" action — edit mode only (page decides). */
    onSeedExampleList?: () => void;
    /** Fires the "open email import" action — both modes. */
    onOpenEmailImport?: () => void;
}

/**
 * Quick-entry dropdown rendered in the search input's inline-start addon.
 * Lists "Usa una lista de ejemplo" (only when the seed handler is provided)
 * and "Importar desde el correo" (only when the import handler is provided).
 * Radix provides menu/menuitem semantics, keyboard nav and Escape-to-close.
 */
const QuickEntryMenu: React.FC<QuickEntryMenuProps> = ({ onSeedExampleList, onOpenEmailImport }) => (
    <DropdownMenu>
        <DropdownMenuTrigger asChild>
            <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="text-primary"
                aria-label="Opciones de entrada rápida"
            >
                <WandSparkles
                    className="h-4 w-4"
                    aria-hidden="true"
                    data-testid="quick-entry-trigger-icon"
                />
            </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
            {onSeedExampleList ? (
                <DropdownMenuItem onSelect={onSeedExampleList}>
                    <ClipboardList className="h-4 w-4" aria-hidden="true" />
                    Usa una lista de ejemplo
                </DropdownMenuItem>
            ) : null}
            {onOpenEmailImport ? (
                <DropdownMenuItem onSelect={onOpenEmailImport}>
                    <Mail className="h-4 w-4" aria-hidden="true" />
                    Importar desde el correo
                </DropdownMenuItem>
            ) : null}
        </DropdownMenuContent>
    </DropdownMenu>
);

/**
 * Right-edge overflow fade for the suggestion badges carousel.
 * Rendered only while embla reports more content to the right (canScrollNext).
 * Uses the semantic `background` token so it adapts to light and dark themes,
 * and is `pointer-events-none` so taps pass through to badges beneath it.
 * MUST be rendered inside a `<Carousel />` (reads its context).
 */
const SuggestionBadgesFade: React.FC = () => {
    const { canScrollNext } = useCarousel();

    if (!canScrollNext) return null;

    return (
        <div
            aria-hidden="true"
            data-testid="suggestion-badges-fade"
            className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-background to-transparent"
        />
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

interface InlineProductSearchProps {
    /** Called immediately when the user clicks a product row */
    onAdd: (item: CreateListItemPayload) => Promise<void>;
    /**
     * When true, hides the "Sin preferencia" option from the results panel.
     * Use this in view-mode (quote-only) contexts where free-text items are not allowed.
     */
    hideFreeOption?: boolean;
    /** Label shown above the search input. Defaults to "¿Qué necesitas?" */
    label?: string;
    /**
     * When provided, renders the quick-entry menu with the seed-example action.
     * Passed only in edit mode — seeding persists items to the list.
     */
    onSeedExampleList?: () => void;
    /** When provided, renders the quick-entry menu with the email-import action. */
    onOpenEmailImport?: () => void;
    /**
     * When true, replaces the rotating idle placeholder with a fixed instruction and
     * renders 3 clickable suggestion badges chosen once at mount (static, no rotation).
     * View-mode instances must leave this false/absent.
     */
    enableSuggestionBadges?: boolean;
}

/**
 * Inline product search for the list detail panel.
 * - Debounces the query 300 ms then calls the backend parser + catalog search
 * - Shows "Sin preferencia" as the FIRST option as soon as the user types,
 *   plus up to 5 real product results (unless hideFreeOption)
 * - Clicking any row immediately adds the item (no intermediate confirmation)
 * - Passes parsed quantity/unit from the backend into the item payload
 */
const InlineProductSearch: React.FC<InlineProductSearchProps> = ({ onAdd, hideFreeOption = false, label = "¿Qué necesitas?", onSeedExampleList, onOpenEmailImport, enableSuggestionBadges = false }) => {
    const searchInputId = useId();
    const [query, setQuery] = useState("");
    const [results, setResults] = useState<ItemSearchResult[]>([]);
    const [attributionToken, setAtributionToken] = useState<string>("");
    const [parsedQuantity, setParsedQuantity] = useState<number>(1);
    const [parsedUnit, setParsedUnit] = useState<string | null>(null);
    const [categoryId, setCategoryId] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [inputFocused, setInputFocused] = useState(false);
    const [activePlaceholder, setActivePlaceholder] = useState<string>(() => pickRandomPlaceholder(""));
    const [suggestionSet] = useState<readonly string[]>(() => pickSuggestionSet(SUGGESTION_BADGE_COUNT));
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const abortRef = useRef<AbortController | null>(null);
    const pendingSavesRef = useRef<Set<string>>(new Set());

    // Debounced search — fires real API call after 700 ms of inactivity, cancels previous in-flight request
    useEffect(() => {
        if (!query.trim() || query.trim().length < 2) {
            setResults([]);
            return;
        }

        if (debounceRef.current) clearTimeout(debounceRef.current);

        debounceRef.current = setTimeout(async () => {
            // Cancel any previous in-flight request
            abortRef.current?.abort();
            abortRef.current = new AbortController();
            const { signal } = abortRef.current;

            setLoading(true);
            try {
                const response = await searchItem(query.trim(), signal, hideFreeOption);
                setResults(response.results);
                setAtributionToken(response.attribution_token);
                setParsedQuantity(response.parsedQuantity);
                setParsedUnit(response.parsedUnit);
                setCategoryId(response.categoryId);
            } catch (err) {
                if (err instanceof Error && err.name === "AbortError") return;
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 700);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            abortRef.current?.abort();
        };
    }, [query]);

    // Reset highlight whenever the query changes (results are stale during debounce)
    useEffect(() => {
        setHighlightedIndex(null);
    }, [query]);

    const hasResults = results.length > 0 || query.trim().length > 0;

    // Rotates to a random suggestion every 5 s while the input is empty and unfocused.
    // On focus the current suggestion freezes in place (no rotation, not swapped back).
    // Disabled in badge mode, where the fixed placeholder replaces rotation.
    useEffect(() => {
        if (enableSuggestionBadges) return;
        if (query.trim() !== "" || inputFocused) return;
        const interval = setInterval(() => {
            setActivePlaceholder((prev) => pickRandomPlaceholder(prev));
        }, 5000);
        return () => clearInterval(interval);
    }, [query, inputFocused, enableSuggestionBadges]);

    /** Clears the search input and dismisses results */
    const handleClear = () => {
        setQuery("");
        setResults([]);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        abortRef.current?.abort();
    };

    /**
     * Optimistically commits an add: clears the input immediately so the user can keep
     * registering products, fires the analytics event, then awaits the save in background.
     * Ignores rapid duplicate submissions of the same selection while its save is pending.
     * @param payload - Item payload to hand to onAdd
     * @param sId - Catalog s_id for the add_to_cart analytics event
     * @param closePicker - Whether to also close the full catalog picker
     */
    const commitAdd = async (payload: CreateListItemPayload, sId?: number, closePicker = false): Promise<void> => {
        const key = buildPayloadKey(payload);
        if (pendingSavesRef.current.has(key)) return;
        pendingSavesRef.current.add(key);

        if (!window.dataLayer) window.dataLayer = [];
        window.dataLayer.push({
            event: "add_to_cart",
            product_id: sId,
            quantity: 1,
            visitor_id: getVisitorId(),
            attribution_token: attributionToken || "",
        });

        // Cancel any pending search — the input is cleared below so results are stale
        if (debounceRef.current) clearTimeout(debounceRef.current);
        abortRef.current?.abort();

        // Optimistic clear: the user can immediately type the next product
        setQuery("");
        setResults([]);
        setHighlightedIndex(null);
        if (closePicker) setPickerOpen(false);

        try {
            await onAdd(payload);
        } catch {
            toast.error(`No se pudo agregar "${payload.name ?? payload.rawDescription}". Intenta de nuevo.`);
        } finally {
            pendingSavesRef.current.delete(key);
        }
    };

    const handleAddProduct = (product: ItemSearchResult) =>
        commitAdd({
            rawDescription: query.trim(),
            catalogProductId: product.id,
            catalogProductSku: product.sku ?? undefined,
            categoryId: product.categoryId,
            quantity: parsedQuantity,
            unit: parsedUnit ?? product.unit ?? null,
            notes: null,
            name: product.name,
        }, product.s_id);

    /**
     * Translates a cart Payload (from ProductSearchPicker) into a CreateListItemPayload
     * and delegates to onAdd, then closes the picker.
     *
     * Uses `payload.hashedSku ?? payload.sku` for catalogProductSku:
     * - hashedSku is the canonical catalog SKU (hashed_sku from ProductData) — what the backend expects.
     * - payload.sku is the store-level SKU — used only as a fallback when hashed_sku is absent.
     */
    const handlePickerProductSelected = (payload: Payload) =>
        commitAdd({
            rawDescription: query.trim() || (payload.name ?? String(payload.id)),
            catalogProductId: payload.id,
            catalogProductSku: payload.hashedSku ?? payload.sku,
            categoryId: payload.category_id,
            quantity: payload.quantity,
            unit: null,
            notes: null,
            name: payload.name ?? undefined,
        }, payload.s_id, true);

    const handleAddSinPreferencia = () =>
        commitAdd({
            rawDescription: query.trim(),
            catalogProductId: null,
            categoryId: categoryId,
            quantity: parsedQuantity,
            unit: parsedUnit ?? null,
            notes: null,
        });

    return (
        <div className="relative w-full min-w-0">
            {/* Search input */}
            <Label htmlFor={searchInputId} className="mb-1.5 block text-sm font-medium">
                {label}
            </Label>
            <InputGroup className="w-full rounded-full px-2 py-2 h-10">
                {(onSeedExampleList || onOpenEmailImport) && (
                    <InputGroupAddon align="inline-start">
                        <QuickEntryMenu
                            onSeedExampleList={onSeedExampleList}
                            onOpenEmailImport={onOpenEmailImport}
                        />
                    </InputGroupAddon>
                )}
                <InputGroupInput
                    id={searchInputId}
                    placeholder={enableSuggestionBadges ? FIXED_PLACEHOLDER : activePlaceholder}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => setInputFocused(true)}
                    onBlur={() => setInputFocused(false)}
                    onKeyDown={(e) => {
                        if (e.key === "Escape") { handleClear(); return; }

                        if (e.key === "ArrowDown") {
                            // "Sin preferencia" is the first selectable option once the user
                            // types; products follow at indexes 1..n. When hideFreeOption is
                            // set, products are the only options (indexes 0..n-1).
                            const hasFreeOption = !hideFreeOption && query.trim().length > 0;
                            const hasOptions = hasFreeOption || results.length > 0;
                            const maxIndex = hasFreeOption ? results.length : results.length - 1;
                            if (loading || !hasOptions) return;
                            e.preventDefault();
                            setHighlightedIndex((prev) =>
                                prev === null ? 0 : prev >= maxIndex ? 0 : prev + 1
                            );
                            return;
                        }

                        if (e.key === "ArrowUp") {
                            const hasFreeOption = !hideFreeOption && query.trim().length > 0;
                            const hasOptions = hasFreeOption || results.length > 0;
                            const maxIndex = hasFreeOption ? results.length : results.length - 1;
                            if (loading || !hasOptions) return;
                            e.preventDefault();
                            setHighlightedIndex((prev) =>
                                prev === null ? maxIndex : prev === 0 ? null : prev - 1
                            );
                            return;
                        }

                        if (e.key === "Enter") {
                            if (!query.trim()) return;
                            e.preventDefault();

                            // Cancel any pending search so Enter is immediate
                            if (debounceRef.current) clearTimeout(debounceRef.current);
                            abortRef.current?.abort();

                            const hasFreeOption = !hideFreeOption && query.trim().length > 0;
                            const productIndex = highlightedIndex === null
                                ? -1
                                : hasFreeOption
                                    ? highlightedIndex - 1
                                    : highlightedIndex;
                            if (highlightedIndex !== null && productIndex >= 0 && productIndex < results.length) {
                                void handleAddProduct(results[productIndex]);
                            } else if (hasFreeOption) {
                                void handleAddSinPreferencia();
                            }
                        }
                    }}
                />
            </InputGroup>

            {/* Suggestion badges — badge mode only, hidden on focus or while typing */}
            {enableSuggestionBadges && query.trim() === "" && !inputFocused && (
                <Carousel
                    opts={{ align: "start" }}
                    className="mt-2"
                    data-testid="suggestion-badges"
                    aria-label="Sugerencias de búsqueda"
                >
                    <CarouselContent className="-ml-2">
                        {suggestionSet.map((suggestion) => (
                            <CarouselItem
                                key={suggestion}
                                className="pl-2 basis-auto shrink-0 whitespace-nowrap"
                            >
                                <Badge variant="outline" asChild>
                                    <button
                                        type="button"
                                        onClick={() => setQuery(suggestion)}
                                        className="cursor-pointer gap-1.5 px-3 py-1.5 text-left hover:bg-accent hover:text-accent-foreground [&>svg]:size-3.5"
                                    >
                                        <ListPlus className="h-3.5 w-3.5" aria-hidden="true" />
                                        {suggestion}
                                    </button>
                                </Badge>
                            </CarouselItem>
                        ))}
                    </CarouselContent>
                    <SuggestionBadgesFade />
                </Carousel>
            )}

            {/* Results panel */}
            {hasResults && (
                <div className="absolute left-0 right-0 z-50 mt-1 rounded-xl border bg-card shadow-md overflow-hidden">
                    <div className="flex flex-col px-1 py-1">
                        {/* Sin preferencia — available as soon as the user types, hidden when hideFreeOption is set */}
                        {!hideFreeOption && query.trim().length > 0 && (
                            <SinPreferenciaRow
                                onAdd={handleAddSinPreferencia}
                                isHighlighted={highlightedIndex === 0}
                                query={query.trim()}
                            />
                        )}

                        {results.length > 0 && (
                            <p className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                ¿Buscas algo en particular?
                            </p>
                        )}

                        {loading ? (
                            <p className="py-4 text-center text-xs text-muted-foreground">Buscando...</p>
                        ) : results.length === 0 ? (
                            <p className="py-4 text-center text-xs text-muted-foreground">
                                Sin resultados para "{query}"
                            </p>
                        ) : (
                            results.map((product, index) => (
                                <ResultRow
                                    key={product.id}
                                    product={product}
                                    onAdd={() => handleAddProduct(product)}
                                    isHighlighted={highlightedIndex === index + 1}
                                />
                            ))
                        )}

                        {/* Buscar producto específico — opens full catalog picker when query is long enough */}
                        {!loading && query.trim().length >= 2 && (
                            <BuscarMasRow onClick={() => setPickerOpen(true)} />
                        )}
                    </div>
                </div>
            )}

            <ProductSearchPicker
                open={pickerOpen}
                onOpenChange={setPickerOpen}
                initialQuery={query}
                onProductSelected={handlePickerProductSelected}
            />
        </div>
    );
};

export { InlineProductSearch };
